import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {Observable, of} from 'rxjs';
import {catchError, exhaustMap, filter, map, mergeMap, tap, withLatestFrom} from 'rxjs/operators';
import * as GlobalMessageActions from '../../core/actions/global-message.actions';
import {GlobalMessage, GlobalMessageType} from '../../core/models/global-message';
import * as AuthActions from '../actions/user.actions';
import {AuthError, AuthErrorCodes} from '../models/auth-error';
import {Authenticate} from '../models/user';
import {AuthState, getRedirectUrl} from '../reducers';
import {AuthService} from '../services/auth.service';
import {TokenStoreService} from '../services/token-store.service';
import {PasswordResetService} from '../services/password-reset.service';

@Injectable()
export class AuthEffects {

    validateAuthToken$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.validateAuthToken),
        map(action => this.tokenService.token),
        tap(token => console.log('token', token)),
        exhaustMap((token: string) => {
                if (token == null || token.length === 0) {
                    return of(AuthActions.validateAuthTokenInvalid());
                }
                return this.validateToken(token);
            }
        )
    ));

    login$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.login),
        map(action => action),
        exhaustMap((loginData: Authenticate) => {
            return this.authService
                .login(loginData)
                .pipe(
                    map(user => AuthActions.loginSuccess({user})),
                    catchError(error => of(AuthActions.loginFailure({authError: error})))
                );
        })
    ));

    loginSuccess$ = createEffect(() => this.actions$
        .pipe(
            ofType(AuthActions.loginSuccess),
            withLatestFrom(this.store.pipe(select(getRedirectUrl))),
            exhaustMap(([action, url]) => {
                let redirect = url;
                if (!url) {
                    redirect = '/';
                }

                if (redirect) {
                    console.log('redirect to', redirect);
                    this.router.navigate([redirect])
                        .then(e => AuthActions.accessGranted());
                }
                return of(AuthActions.accessGranted());
            })
        ));

    loginFailure$ = createEffect(() => this.actions$
        .pipe(
            ofType(AuthActions.loginFailure),
            filter(action => action.authError.isGlobalMessage),
            mergeMap((action) => {
                    const message: GlobalMessage = {
                        id: 'auth-login-failure',
                        type: GlobalMessageType.ERROR,
                        message:
                            `The login process couldn't be performed: ${action.authError.errorMessage} [${action.authError.errorCodeFormatted}]`
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));

    loginRedirect$ = createEffect(() => this.actions$
        .pipe(
            ofType(AuthActions.accessDenied),
            tap(() => {
                return this.router.navigate(['/login']);
            })
        ), {dispatch: false}
    );

    logout$ = createEffect(() => this.actions$
        .pipe(
            ofType(AuthActions.logout),
            tap(() => {
                this.authService.logout();
                return this.router.navigate(['/login']);
            })
        ), {dispatch: false});

    constructor(private actions$: Actions,
                private authService: AuthService,
                private passwordResetService: PasswordResetService,
                private tokenService: TokenStoreService,
                private router: Router,
                private store: Store<AuthState>) {
    }

    private validateToken(token): Observable<any> {
        return this.authService.getUser().pipe(
            exhaustMap(user => of(AuthActions.validateAuthTokenLoggedIn({user: user}))),
            catchError((error: AuthError) => {
                    console.log('validate token, got auth error', error);

                    const errorActions = [];
                    errorActions.push(AuthActions.validateAuthTokenInvalid());

                    switch (error.errorCode) {
                        // session expired ->
                        case AuthErrorCodes.InvalidCredentials:
                            errorActions.push(AuthActions.logout({requestedUrl: this.router.url}));
                            break;
                        // dispatch general error
                        default:
                            const message: GlobalMessage = {
                                id: 'validate-auth-token-error',
                                type: GlobalMessageType.ERROR,
                                message: `The auth token couldn't be validated: ${error.errorMessage}`
                            };
                            errorActions.push(GlobalMessageActions.addMessage({globalMessage: message}));
                    }
                    return errorActions;
                }
            )
        );
    }
}
